import { navigate, PATHS } from '@client/routes';
('use strict');

import $ from 'jquery';
import { customerIOReset } from '@client/new/services/customerio';

import * as enums from '@playbook-technologies/common-enums';
import { getCreatorApiUrl } from './api-urls';
import { PermissionScope } from '@playbook-technologies/common-enums';
import { isEmpty } from 'lodash';

const SUPER_ADMINS = ['wojo@playbookapp.io', 'postman@playbookapp.io']; // TODO: replace with new role

export const isProduction = () => import.meta.env.VITE_ENV === 'production';

export interface User {
  username: string;
  role: number;
  firstName: string;
  lastName: string;
  email: string;
  token: string;
  tokenExpiresAt: string;
  influencerId?: any;
  influencer?: { firstName: string; lastName: string; id: number };
  id: number;
}

const auth = {
  //WARNING! returns Promise
  login(username, password) {
    //console.log('[Auth] login() called.');
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const reactThis = this;
    return new Promise((resolve, reject) => {
      $.ajax({
        url: getCreatorApiUrl('/admin-login'),
        data: { username: username, password: password },
        type: 'POST',
        success: function (data) {
          //console.log('[Auth] User logged in, saving data to local storage...');
          const user: any = JSON.stringify(data);
          localStorage.user = user;
          resolve('Logged in. Loading dashboard...');
          window.dispatchEvent(new Event('User.login()'));
          reactThis.onAuthenticationChange(true, data);
        },
        error: function (e) {
          console.log('[Auth] Unsuccessful login.', e);
          if (e.status === 401) {
            reject('Wrong credentials; please try again.');
            reactThis.onAuthenticationChange(false, null);
          } else {
            reject(
              'You are experiencing connection issues, or our servers are offline. Please check your internet connection, or try again later.',
            );
            reactThis.onAuthenticationChange(false, null);
          }
        },
      });
    });
  },

  getInfluencerById(id) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const reactThis = this;
    return new Promise((resolve, reject) => {
      const url = `/admin-influencers/${id ? id : ''}`;
      $.ajax({
        url: getCreatorApiUrl(url),
        type: 'GET',
        beforeSend(xhr) {
          xhr.setRequestHeader('Authorization', reactThis.getToken());
        },
        success(data) {
          console.log('Fetched influencer info by id');
          resolve(data);
        },
        error(e) {
          if (e.status === 401) {
            console.log('Unauthorized');
            reject('auth');
            return;
          }
          console.log('[Ajax] Error fetching Influencers.', e);
          reject(e);
        },
      });
    });
  },

  getOTPCode(email) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const reactThis = this;
    return new Promise((resolve, reject) => {
      $.ajax({
        url: getCreatorApiUrl('/admin-email-send'),
        data: { email: email },
        type: 'POST',
        success: function (data) {
          resolve('OTP code sent. Check mailbox');
          reactThis.onAuthenticationChange(true, data);
        },
        error: function (e) {
          console.log('[Auth] Could not send OTP code.', e);
          reject('Could not send OTP code.');
        },
      });
    });
  },

  verifyOTPCode(email, code) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const reactThis = this;
    return new Promise((resolve, reject) => {
      $.ajax({
        url: getCreatorApiUrl('/admin-email-confirm'),
        data: { email: email, otp: code },
        type: 'POST',
        success: function (data) {
          console.log('[Auth] OTP code verified, logging in...');
          localStorage.user = JSON.stringify(data);
          resolve('Logged in. Loading dashboard...');
          reactThis.onAuthenticationChange(true, data);
        },
        error: function (e) {
          console.log('[Auth] Could not verify OTP code', e);
          if (e.status) {
            reject({ status: e.status, message: e.responseText });
          }
          reject({ status: 500, message: 'Server error' });
        },
      });
    });
  },

  getToken() {
    //console.log('[Auth] getToken() called.');
    if (localStorage && localStorage.user) {
      const user = JSON.parse(localStorage.user);
      return user.token;
    } else {
      return null;
    }
  },

  getUser(): User {
    if (localStorage && localStorage.user) {
      return JSON.parse(localStorage.user);
    } else {
      return null;
    }
  },

  logout() {
    //console.log('[Auth] logout() called.');
    delete localStorage.user;
    customerIOReset();
    this.onAuthenticationChange(false, null);
    navigate(PATHS.LOGIN);
  },

  // DEPRECATED - use useUser() hook instead
  isLoggedIn() {
    //console.log('[Auth] isLoggedIn() called.');
    // check if localStorage exists first, because server-side rendering
    if (localStorage && localStorage.user) {
      if (new Date(this.getUser().tokenExpiresAt) < new Date()) {
        console.log('Stored token has expired, please log back in.');
        return false;
      } else {
        //console.log('User is logged in.');
        return true;
      }
    } else {
      return false;
    }
  },

  isSuperAdministrator() {
    return (
      localStorage.user &&
      this.getUser().role === enums.PortalUser.Role.Administrator.value &&
      (!isProduction() ||
        (isProduction() && SUPER_ADMINS.includes(this.getUser().email)))
    ); // TODO: replace that with a new role
  },

  isAdministrator() {
    if (
      localStorage.user &&
      this.getUser().role === enums.PortalUser.Role.Administrator.value
    )
      return true;
    return false;
  },

  isContentEditor() {
    if (
      localStorage.user &&
      this.getUser().role === enums.PortalUser.Role.ContentEditor.value
    )
      return true;
    return false;
  },

  isModerator() {
    if (
      localStorage.user &&
      this.getUser().role === enums.PortalUser.Role.Moderator.value
    )
      return true;
    return false;
  },

  isInfluencer() {
    if (
      localStorage.user &&
      this.getUser().role === enums.PortalUser.Role.Influencer.value
    )
      return true;
    return false;
  },

  isProduction,
  // NOTE: this function will get replaced by the Application component to handle changes in auth state
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onAuthenticationChange: function () {},

  async isVideoLibraryFeatureEnabled(influencerId) {
    try {
      const givenInfluencerId = parseInt(influencerId)
        ? parseInt(influencerId)
        : this.getUser().influencerId;
      if (!givenInfluencerId) {
        return false;
      }
      const influencer = await this.getInfluencerById(givenInfluencerId);
      return (
        influencer &&
        influencer.customizations &&
        influencer.customizations.isNewVideo
      );
    } catch (err) {
      return false;
    }
  },
  hasStripePermission() {
    const user = this.getUser();
    if (!user?.permissions || isEmpty(user?.permissions)) return false;

    return user.permissions.includes(PermissionScope.Stripe);
  },
  hasPayoutsPermission() {
    const user = this.getUser();
    if (!user?.permissions || isEmpty(user?.permissions)) return false;

    return user.permissions.includes(PermissionScope.Payouts);
  },
  getCustomPermissions() {
    const user = this.getUser();
    if (!user?.permissions || isEmpty(user?.permissions)) return [];

    return user.permissions;
  },
};

export default auth;
